import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppDispatch, useAppSelector } from '../../../../app/hook';
import { useLazyGetProfileQuery, useUpdateEmailMutation } from '../../../../app/services/auth-rtk.service';
import Alert, { IAlertType } from '../../../../components/common/Alert';
import { AlertsType } from '../../../../constants/alerts.constant';
import { selectUserProfile, updateEmail } from '../../../../features/auth/auth.slice';

type ProfileEditEmail = {
  user_email: string;
};

const ProfileChangeEmail = ({ showModal, setShowModal }: { showModal?: boolean; setShowModal?: (show: boolean) => void }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const profile = useAppSelector(selectUserProfile);

  const schema = Yup.object({
    user_email: Yup.string()
      .required()
      .matches(/^[^\s@`!@#$%^&*()=]+@[^\s@`!@#$%^&*()=]+\.[^\s@`!@#$%^&*()=]+$/, t(AlertsType.ISATKO003))
      .email()
      .label('email'),
  });

  const [alert, setAlert] = useState<IAlertType>({
    isShow: false,
    title: '',
    type: 'warning',
    message: '',
  });

  const [updateProfileEmail, { isLoading, isError, isSuccess, reset: resetUpdateProfileEmail }] = useUpdateEmailMutation();

  const [checkEmailStatus, { isLoading: isValidatinEmail }] = useLazyGetProfileQuery();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<ProfileEditEmail>({
    defaultValues: {
      user_email: profile?.user_email,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    closeAlert();

    updateProfileEmail({
      new_user_email: data.user_email,
      new_user_email_confirmation: data.user_email,
      user_email: profile?.user_email,
    })
      .unwrap()
      .then(value => {
        setAlert({
          isShow: true,
          message: t(AlertsType.ISPFKO001),
          type: 'success',
          title: '',
        });
      })
      .catch(res => {
        if (res.status != 500 && res.data) {
          setAlert({
            ...alert,
            isShow: true,
            message: res.data.message,
          });
        }
      });
  };

  const closeAlert = () => {
    setAlert({
      isShow: false,
      title: '',
      type: 'warning',
      message: '',
    });
  };

  const handleNew = () => {
    closeAlert();
    resetUpdateProfileEmail();
    reset();
  };

  const handleCheckIfVerified = () => {
    checkEmailStatus()
      .unwrap()
      .then(res => {
        if (res.user_email === getValues('user_email')) {
          dispatch(updateEmail(res.user_email));
          closeAlert();
          handleNew();
          setShowModal?.(false);
        } else {
          toast.error(t('ProfileChangeEmail/key6'));
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  useEffect(() => {
    reset({ user_email: profile?.user_email });
  }, [profile?.user_email]);

  return (
    <Modal show={showModal} onHide={() => setShowModal?.(false)} centered onExiting={() => reset()} size="lg">
      <Modal.Header className="tw-text-zinc-900 tw-text-4xl tw-font-bold tw-font-['Sofia Sans'] tw-leading-[48px] tw-items-start tw-justify-between">
        {!isSuccess ? t('ProfileChangeEmail/key1') : t('ProfileChangeEmail/key2')}
        <PSButton
          id="btn-profile-change-modal"
          variant="none"
          className="tw-text-gray-600 tw-p-0 tw-border-0 tw-bg-white"
          onClick={() => {
            reset();
            setShowModal?.(false);
          }}
          icon={<BiX size={22} />}
        />
      </Modal.Header>
      <Modal.Body>
        <b className="tw-text-zinc-900 tw-text-lg tw-font-bold tw-font-['Sofia Sans'] tw-leading-7 tw-mb-6">
          {!isSuccess ? (
            t('ProfileChangeEmail/key3')
          ) : (
            <p className="tw-text-zinc-900 tw-text-lg tw-font-bold tw-font-['Sofia Sans'] tw-leading-7 tw-mb-6">
              {t('ProfileChangeEmail/text-01-1')} <b>{getValues('user_email')}</b> {t('ProfileChangeEmail/text-01-2')}
            </p>
          )}
        </b>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {alert.isShow && alert.type === 'warning' && (
            <Alert type={alert.type} onClick={() => closeAlert()}>
              {alert.message}
            </Alert>
          )}

          <Form.Group className="my-4">
            <Form.Label>{t('ProfileChangeEmail/l-01')}</Form.Label>
            <Form.Control isInvalid={!!errors.user_email || isError} isValid={isSuccess} {...register('user_email')} />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.user_email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex flex-wrap align-items-center tw-gap-3">
            {!isSuccess ? (
              <PSButton
                id="btn-profile-change-submit"
                variant="primary"
                type="submit"
                className="me-2"
                disabled={isLoading}
                isLoading={isLoading}
                loadingAnimation="grow"
                title={t('ProfilePage/btn-04')}
              />
            ) : (
              <PSButton
                id="btn-profile-change-check"
                variant="primary"
                type="button"
                className="me-2"
                disabled={isValidatinEmail}
                onClick={handleCheckIfVerified}
                isLoading={isValidatinEmail}
                loadingAnimation="grow"
                title={t('ProfileChangeEmail/key4')}
              />
            )}

            {isSuccess && (
              <PSButton
                id="btn-profile-change-new"
                className="btn btn-info text-decoration-none tw-bg-[#E6EDFD]"
                onClick={handleNew}
                title={t('ProfileChangeEmail/key5')}
              />
            )}
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default WrappedErrorBoundary(ProfileChangeEmail);
